// โหลดแผนที่ฐานและกริดภูมิศาสตร์ครั้งเดียวต่อการเปิดหน้า (docs/PLAN.md ข้อ 4.4)
// โหลดไม่ได้ก็เล่นต่อได้ตามปกติ เพียงแต่ไม่มีแผนที่และตำแหน่ง

import { useEffect, useState } from "react";
import { GRID_VERSION, parseGrid, type GeoGrid } from "../geo/geodata";
import { parseProvinces, type Province, type ProvinceFile } from "../geo/provinces";
import { resolveRange, type SpeciesRange } from "../geo/range";
import type { SpeciesCard } from "../shared/api";

export interface WorldShapes {
  width: number;
  height: number;
  sphere: string;
  graticule: string;
  countries: { code: string; d: string }[];
}

export interface GeoData {
  world: WorldShapes;
  grid: GeoGrid;
}

export type GeoState = { status: "loading" } | { status: "ready"; data: GeoData } | { status: "error" };

let pending: Promise<GeoData> | null = null;

async function gunzip(res: Response): Promise<Uint8Array> {
  if (typeof DecompressionStream === "undefined" || !res.body) throw new Error("เบราว์เซอร์นี้คลายไฟล์แผนที่ไม่ได้");
  return new Uint8Array(await new Response(res.body.pipeThrough(new DecompressionStream("gzip"))).arrayBuffer());
}

export function loadGeo(): Promise<GeoData> {
  if (!pending) {
    pending = Promise.all([fetch("/geo/world.json"), fetch(`/geo/grid.bin.gz?v=${GRID_VERSION}`), loadProvinces()]).then(async ([w, g, provinces]) => {
      if (!w.ok || !g.ok) throw new Error("โหลดแผนที่ไม่สำเร็จ");
      return { world: (await w.json()) as WorldShapes, grid: { ...parseGrid(await gunzip(g)), provinces } };
    });
    pending.catch(() => (pending = null)); // ครั้งหน้าลองโหลดใหม่
  }
  return pending;
}

/** เส้นเขตจังหวัดในไทย โหลดไม่ได้ก็ยังเล่นได้ แค่บอกชื่อประเทศแทนจังหวัด */
function loadProvinces(): Promise<Province[] | undefined> {
  return fetch("/geo/th-provinces.json")
    .then((res) => (res.ok ? (res.json() as Promise<ProvinceFile>) : null))
    .then((file) => (file ? parseProvinces(file) : undefined))
    .catch(() => undefined);
}

export function useGeo(): GeoState {
  const [state, setState] = useState<GeoState>({ status: "loading" });
  useEffect(() => {
    let live = true;
    loadGeo().then(
      (data) => live && setState({ status: "ready", data }),
      () => live && setState({ status: "error" }),
    );
    return () => {
      live = false;
    };
  }, []);
  return state;
}

const ranges = new Map<string, SpeciesRange | null>();

/** ถิ่นที่อยู่ของชนิดนี้เป็นช่องกริด (คำนวณครั้งเดียวต่อชนิด) */
export function rangeOf(card: SpeciesCard, grid: GeoGrid): SpeciesRange | null {
  const key = `${card.species.id}:${card.rangeCells ?? card.countries.join(",")}`;
  if (!ranges.has(key)) ranges.set(key, resolveRange(card, grid, card.species.traits));
  return ranges.get(key)!;
}
